import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { chatReadEpoch, invalidateChatReadResponses, pendingChatReadCount } from './chat-session';

// API is always mounted under /orion-web on both brands (see lib/use-credits).

/**
 * The unread-notification count behind the bells — ONE shared store for every
 * mounted bell, so a route change or a mark costs one request, not one per bell.
 *
 * The number is the server's authoritative UNREAD count
 * (`GET /api/notifications?countOnly=1` → `unreadCount` = UNSEEN + SEEN_BUT_UNREAD)
 * — the SAME definition and the same feed the native apps badge off, not a count
 * derived from the loaded rows (the platform counts rows the query never returns,
 * so a list-derived count under-reports). `countOnly` skips the 50-row feed query
 * a bell would throw away.
 *
 * WhatsApp semantics: the dot tracks what you haven't READ, not what you haven't
 * peeked at. Opening the inbox marks rows SEEN_BUT_UNREAD, which this count still
 * includes — so visiting /notifications does NOT clear the dot; reading a row (or
 * mark-all-read) is what drops it.
 *
 * Failures leave the last known count: a 401/5xx rejects, and an unknown
 * server-side count (`unreadCount: null`, i.e. the UNSEEN count failed — native's
 * nil) is likewise ignored rather than rendered as 0. The initial value is 0, so a
 * failed first load simply shows no dot.
 */

let count = 0;
/** A tap decremented the badge and its mark hasn't been reconciled yet. */
let optimisticPending = false;
/** Only the newest read may write the count — an older one in flight is stale. */
let generation = 0;
let inflight: Promise<void> | null = null;

const listeners = new Set<() => void>();
let poll: ReturnType<typeof setInterval> | undefined;
const foregroundRefresh = () => {
  if (document.visibilityState !== 'visible' || pendingChatReadCount() > 0) return;
  invalidateChatReadResponses();
  void loadUnreadCount(true);
};

export function subscribeUnreadCount(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', foregroundRefresh);
    poll = setInterval(foregroundRefresh, 30_000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', foregroundRefresh);
      clearInterval(poll);
    }
  };
}

export function getUnreadCount(): number {
  return count;
}

/** SSR never has a count: module state is per-process, so it must not leak between users. */
export function getServerUnreadCount(): number {
  return 0;
}

function setCount(next: number): void {
  if (next === count) return;
  count = next;
  for (const listener of listeners) listener();
}

/**
 * Optimistic -1 for a row tap: the number drops on the tap itself, before the
 * mark is persisted (native decrements the same way, before its write).
 * Clamped at 0. Call it only for a row that is actually unread.
 *
 * The tap also navigates, and that route change fires a read that BEATS the
 * mark's POST — it would return the still-lit count. So while a mark is pending
 * every non-authoritative read drops its result; the authoritative re-read after
 * the POST settles (`refreshUnreadNotifications`) is what reconciles.
 */
export function decrementUnreadNotifications(): void {
  optimisticPending = true;
  setCount(Math.max(0, count - 1));
}

/**
 * Re-read the count as authoritative. Call it once a read-state mutation has
 * settled on the server (mark-all-read, or a row mark whose POST resolved or
 * failed) — its result supersedes any optimistic decrement.
 */
export function refreshUnreadNotifications(): void {
  void loadUnreadCount(true);
}

/** A server mutation receipt supersedes count requests started before it. */
export function applyUnreadNotificationReceipt(next: number | null): void {
  generation += 1;
  inflight = null;
  optimisticPending = false;
  if (typeof next === 'number' && Number.isFinite(next)) setCount(Math.max(0, next));
}

/**
 * Read the count. Non-authoritative reads (mount, route change) are
 * single-flighted, so the mounted bells collapse to one request.
 */
export function loadUnreadCount(authoritative = false): Promise<void> {
  if (pendingChatReadCount() > 0) return Promise.resolve();
  if (!authoritative && inflight) return inflight;
  const promise = runLoad(authoritative).finally(() => {
    if (inflight === promise) inflight = null;
  });
  inflight = promise;
  return promise;
}

async function runLoad(authoritative: boolean): Promise<void> {
  const gen = ++generation;
  const readEpoch = chatReadEpoch();
  let next: number | null = null;
  try {
    next = await fetchCount();
  } catch {
    next = null;
  }
  if (gen !== generation || readEpoch !== chatReadEpoch() || pendingChatReadCount() > 0) return;
  if (authoritative) optimisticPending = false;
  if (next === null || (!authoritative && optimisticPending)) return;
  setCount(Math.max(0, next));
}

async function fetchCount(): Promise<number | null> {
  const res = await apiFetch(`${API_BASE}/api/notifications?countOnly=1`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { unreadCount?: number | null };
  return typeof data.unreadCount === 'number' ? data.unreadCount : null;
}

/** Test seam — resets the module-level count/flags between cases. */
export function resetUnreadCountStore(): void {
  count = 0;
  optimisticPending = false;
  generation += 1;
  inflight = null;
  listeners.clear();
  clearInterval(poll);
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', foregroundRefresh);
}
